/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a demand source declares it does not know** — the coverage claim, assembled from the
 * editors that own each plane (`TM4`, `#1170`).
 *
 * A template has to be able to say *"Magnite needs `app.publisher.id` and only you have it"*. Before
 * this the claim reached exactly one destination of one type — a query-parameter row's `suppliedBy`
 * — and the destination OpenRTB actually uses could not make it at all: a `MappingLeaf` is
 * `static | macro | expression`, so an unfilled leaf is an empty string on the wire rather than a
 * declaration.
 *
 * ## One assembler, every plane
 *
 * Each editor owns a plane and knows **all** of its slots, which is what makes the whole side-car
 * reconstructible from a form. That matters because the storage rule is *replace, or keep what is
 * stored* (`ArtifactWrite.authoring`, the same rule `readiness` follows): a surface that edits any
 * plane sends **all** of them, so a partial patch cannot silently un-declare what another editor
 * stated. Assembling here rather than at each save site is what keeps that promise checkable — the
 * alternative is the shape CLAUDE.md already names, *a rule attached to one write path lapses the
 * moment another is added*.
 *
 * ## The endpoint is a plane, and that is what retires `hasUnfilledEndpointPlaceholder`
 *
 * That validator existed because the catalogue it belonged to could not express the class
 * (`destination-field.ts`'s own header says so). It asked *does this string still contain
 * `{braces}`* — a regex standing in for a declaration — and it was **written twice**, once in
 * `libs/adapters` and once in the console, which is how a rule with no shared home goes.
 *
 * With the class declared, the endpoint is just another slot: a template whose endpoint is a shape
 * says `endpoint` is operator-supplied, and *is anything still unfilled* becomes one question asked
 * of every plane at once — parameters and body leaves included, which the regex could never reach.
 */

import {
  endpointHasPlaceholder,
  operatorSuppliedSlots,
  slotFor,
  unfilledEndpointPlaceholders,
  type ArtifactAuthoringDto,
} from '@artifact-authoring';
import { isRequiredHere } from '@destination-field';

export { endpointHasPlaceholder, unfilledEndpointPlaceholders };

import type { MappingLeaf } from '../components/shared/editors/ortb-body-editor.adapters';
import { pathToProvenanceKey } from '../components/shared/editors/ortb-body-editor.adapters';
import type { ParameterRow } from '../components/shared/editors/parameter-editor.adapters';
import type { HeaderRow } from '../components/shared/editors/headers-editor.adapters';

/** What the assembler needs from a form. Deliberately the editors' own shapes, not a third model. */
export interface CoverageSources {
  endpoint?: string;
  parameterRows?: readonly ParameterRow[];
  headerRows?: readonly HeaderRow[];
  bodyLeaves?: readonly MappingLeaf[];
}

/**
 * Has this leaf been given anything at all?
 *
 * **Not `String(value) !== ''`**, which is where this started and what the linter caught:
 * `staticValue` is `unknown`, and a body leaf legitimately holds an object or an array (the guided
 * view edits a scalar array inline). Coercing turns an empty `{}` into `'[object Object]'` and
 * counts an unfilled slot as supplied — the direction that fails **silently**, since a slot the
 * operator still has to fill is reported as done and nothing on the screen says otherwise.
 */
function leafHasValue(leaf: MappingLeaf): boolean {
  if (leaf.kind !== 'static') return Boolean(leaf.expression);
  const value = leaf.staticValue;
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/**
 * **There is no side-car for the body's `required` any more** (`#1184`, 2026-08-27).
 *
 * `authoringFromForm` and `leavesWithRequired` lived here for one day. They existed because a body
 * leaf had nowhere to keep an authoring claim — the premise being *the body is sent verbatim* —
 * and the node form disproved it. Worse, the round trip never closed: `leavesToTemplate` produces
 * the document, the editor hands the document up, and a side-car field was **dropped on the way
 * out**, so the checkbox persisted nothing.
 *
 * The claim now rides on the leaf's node as `$optional`, beside `$suppress` and `$valueType`, and is
 * stripped with them. One home, and the same round trip every other property takes.
 */

/**
 * **Which declared operator slots still have nothing in them** (`R6`, and `needsOperatorValue`'s
 * caller).
 *
 * The question `hasUnfilledEndpointPlaceholder` was asking, now asked of every plane. It is a
 * **derived negative** — *this cannot work yet* — which is the only direction `E1` permits a derived
 * claim to point, and the reason it may gate the Prove phase where a derived *positive* never could.
 *
 * A template's own unfilled slots are the correct and expected state: a template is a shape. Only an
 * instance is judged, which is the caller's decision rather than this function's.
 */
export function unfilledOperatorSlots(
  authoring: ArtifactAuthoringDto | undefined | null,
  sources: CoverageSources,
): string[] {
  // **Every plane's claim, from wherever that plane keeps it** — a preset's declaration through
  // `operatorSuppliedSlots`, and a row's own `importance`, which is where that plane keeps it.
  // Reading only the side-car would make an unfilled required parameter invisible the moment the
  // checkbox stopped writing one.
  //
  // **It reads `importance`, never `!optional`** (`X46`, 2026-09-02). Until then this gate keyed on
  // the *omission* answer, so an operator who correctly chose *omit it rather than send `pubid=`*
  // silently switched off the warning that the id was blank — choosing the right wire behaviour
  // turned off the validation. The two claims are separate slots now.
  const declared = [
    ...new Set([
      // A vendor preset's own `from: 'operator'` declaration — the only thing the side-car carries.
      ...operatorSuppliedSlots(authoring),
      // Every other plane keeps the claim on the thing it is about, and is read from there.
      ...(sources.parameterRows ?? [])
        .filter((row) => isRequiredHere(row.importance) && row.name.trim() !== '')
        .map((row) => slotFor('param', row.name.trim())),
      // A header row keeps the claim on itself too (`#1354`, `4b-ii`). *Never send* is a decision about
      // the header, so a suppressed row has nothing to fill.
      ...(sources.headerRows ?? [])
        .filter((row) => isRequiredHere(row.importance) && !row.suppress && row.key.trim() !== '')
        .map((row) => slotFor('header', row.key.trim())),
      ...(sources.bodyLeaves ?? [])
        .filter((leaf) => isRequiredHere(leaf.importance))
        .map((leaf) => slotFor('body', pathToProvenanceKey(leaf.path))),
      ...(endpointHasPlaceholder(sources.endpoint) ? [slotFor('endpoint')] : []),
    ]),
  ];
  if (declared.length === 0) return [];

  const filled = new Set<string>();
  if (sources.endpoint?.trim() && !endpointHasPlaceholder(sources.endpoint)) filled.add(slotFor('endpoint'));
  for (const row of sources.parameterRows ?? []) {
    // **A row is filled if it has an answer, not if it has a literal** (`N11f`). Reading only
    // `value` was already the wrong question and the split made it visible: `cb` is a macro row
    // with nothing in `value`, so a source that maps every field it declares reported the
    // mapped ones as still needing the operator.
    const answered = String(row.value ?? '').trim() !== '' || !!row.expression;
    if (answered) filled.add(slotFor('param', row.name.trim()));
  }
  for (const row of sources.headerRows ?? []) {
    const answered = row.value.trim() !== '' || !!row.expression;
    if (answered) filled.add(slotFor('header', row.key.trim()));
  }
  for (const leaf of sources.bodyLeaves ?? []) {
    if (leafHasValue(leaf)) filled.add(slotFor('body', pathToProvenanceKey(leaf.path)));
  }

  return declared.filter((slot) => !filled.has(slot));
}
